import { Vault } from "../../libs/storage.js";
import { uuidv7 } from "../utils/uuidv7";

export { EventIdentity, SCHEDULE_EVENT_ID_PREFIX, normalizeLeadEmail };

/**
 * Identidad canónica de un evento de conversión (`event_id`).
 *
 * REGLA: el id se acuña UNA vez, en el origen del evento, y acompaña a todas
 * sus copias — píxel de navegador, `dataLayer` (GTM) y CAPI server-to-server.
 * Con el mismo id en las dos copias, la dedup nativa de las redes (Meta:
 * event_name + event_id, 48 h; OpenAI: píxel + nombre + event_id, gana el
 * primero) hace el trabajo y la redundancia deja de ser doble conteo.
 *
 * - Lead: uuidv7 acuñado en `do('lead')` ANTES del POST. Viaja en el mensaje
 *   del lead (`event_id`, body:true) hasta `user_optin.browser_event_id` y de
 *   ahí, por webhook, a `conversion_event.event_id` en marketing.
 * - Cita: NADIE lo acuña, se DERIVA del uuid del evento de Calendly, que ya
 *   comparten navegador y CRM (`opportunity.external_opportunity_id_reference`).
 * - Resto de eventos de navegador (page_view, content_view, video_watched_50):
 *   uuidv7 por página y evento; solo sirve para conciliar en ClickHouse.
 */

const LEAD_ID_KEY_PREFIX = "pd_evt_lead_";
// Ventana de reuso. El CRM (addUserOptin) deduplica por
// `user_id + tracking_id + experiment_id` dentro de estos 20 min: un doble
// submit es UN user_optin y por tanto tiene que ser UN evento de red. Ojo: la
// clave de aquí tiene que llevar ESAS MISMAS dimensiones (ver `scope` en
// forLead) — con la clave solo por email, el mismo visitante que vuelve dentro
// de la ventana con otra atribución generaba un segundo user_optin real con el
// id del primero y la red se comía esa conversión.
const LEAD_ID_TTL = "20m";
const LEAD_ID_TTL_MS = 20 * 60 * 1000;

const SCHEDULE_EVENT_ID_PREFIX = "opportunity_created:cal:";

// `event_id` en el schema del lead: string de 64 como mucho.
const MAX_EVENT_ID_LENGTH = 64;

// Respaldo en memoria del reuso por email: con storage bloqueado (issue 1J)
// el Vault devuelve false SIEMPRE y sin esto cada reintento acuñaría un id
// nuevo dentro de la misma página.
const memoryLeadIds: Record<string, { id: string; expiresAt: number }> = {};

function normalizeLeadEmail(email: unknown): string {
  if (typeof email !== "string") {
    return "";
  }
  return email.trim().toLowerCase();
}

/**
 * Hash corto y SÍNCRONO (FNV-1a de 32 bits, dos semillas) para construir la
 * clave de reuso. No es criptografía y no lo pretende: su trabajo es que el
 * email no viaje EN CLARO como nombre de clave. Cuando el localStorage está
 * bloqueado (el escenario que justifica el propio Vault) la clave se convierte
 * en el NOMBRE de una cookie del dominio de la landing, que el navegador manda
 * en la cabecera `Cookie` de cada petición durante 20 min: el email acabaría
 * legible en los access logs del CDN del tenant. El valor —un uuid— sí puede
 * ir tal cual.
 *
 * Síncrono a propósito: `forLead` corre en el camino del optin, justo antes
 * del POST, y `crypto.subtle` es asíncrono.
 */
function normalizeEventId(candidate: unknown): string {
  if (typeof candidate !== "string") {
    return "";
  }
  return candidate.trim().slice(0, MAX_EVENT_ID_LENGTH);
}

function shortHash(value: string): string {
  return fnv1a(value, 0x811c9dc5) + fnv1a(value, 0x7b3f8a11);
}

function fnv1a(value: string, seed: number): string {
  let hash = seed;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

const EventIdentity = {
  /** uuidv7 fresco (page_view, content_view, video_watched_50...). */
  mint(): string {
    return uuidv7();
  },

  /**
   * Normaliza un `event_id` que trae el emisor (o que vamos a acuñar) a lo que
   * cabe en el contrato: string recortado a 64. Se aplica en el ORIGEN, no al
   * serializar el mensaje: truncar solo en el mensaje hacía que un id largo
   * llegara entero al píxel y recortado a la CAPI — dos cadenas distintas, o
   * sea, dedup rota en silencio, que es justo lo contrario de lo que persigue
   * todo esto.
   */
  normalize(candidate: unknown): string {
    return normalizeEventId(candidate);
  },

  /**
   * Identidad del optin. Reutiliza el id del mismo email normalizado durante
   * 20 min DENTRO DEL MISMO `scope` (las dimensiones con las que deduplica el
   * CRM: atribución + experimentos); sin email (optin solo con teléfono) acuña
   * uno nuevo — no hay clave con la que reutilizar y duplicar es peor que no
   * deduplicar.
   */
  forLead(email?: unknown, scope?: unknown): string {
    const normalized = normalizeLeadEmail(email);
    if (normalized === "") {
      return uuidv7();
    }

    const scopeText = typeof scope === "string" ? scope : "";
    const key = LEAD_ID_KEY_PREFIX + shortHash(normalized + "|" + scopeText);

    try {
      const stored = Vault.get(key);
      if (typeof stored === "string" && stored !== "") {
        return stored;
      }
    } catch (e) {
      // storage denegado: seguimos por la copia en memoria
    }

    const cached = memoryLeadIds[key];
    if (cached && cached.expiresAt > Date.now()) {
      return cached.id;
    }

    const minted = uuidv7();
    memoryLeadIds[key] = {
      id: minted,
      expiresAt: Date.now() + LEAD_ID_TTL_MS,
    };
    try {
      Vault.set(key, minted, { expires: LEAD_ID_TTL });
    } catch (e) {
      // best-effort: la copia en memoria cubre esta página
    }
    return minted;
  },

  /**
   * Identidad de la cita, DERIVADA del uuid del evento de Calendly: el CRM
   * construye exactamente la misma cadena desde
   * `opportunity.external_opportunity_id_reference`. Una reagenda trae un
   * uuid nuevo → id nuevo en ambos lados, que es lo correcto.
   */
  forSchedule(eventUuid: unknown): string {
    if (typeof eventUuid !== "string" || eventUuid === "") {
      return "";
    }
    // Recortado a 64 como cualquier otro id: es lo que cabe en
    // `conversion_event.event_id` del lado servidor, y las dos copias tienen
    // que ser la MISMA cadena (un uuid normal deja la cadena en 60).
    return normalizeEventId(SCHEDULE_EVENT_ID_PREFIX + eventUuid);
  },
};
